import { createFileRoute, Link } from "@tanstack/react-router";
import { ContentPage, ContentSection, BiText } from "@/components/layout/ContentPage";
import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/about")({
  head: () => {
    const seo = buildSeo({
      title: "About Bangla Calculator — Made for Bangladesh",
      description:
        "About Bangla Calculator — 51+ free bilingual calculators for Bangladesh: secular, Islamic (Zakat, Prayer Times, Faraid), and BD-specific tools.",
      path: "/about",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "About", path: "/about" },
      ],
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: AboutPage,
});

function AboutPage() {
  return (
    <ContentPage
      titleEn="About Bangla Calculator"
      titleBn="বাংলা ক্যালকুলেটর সম্পর্কে"
      introEn="Bangla Calculator is a free, bilingual collection of 51+ calculators built specifically for Bangladesh — covering finance, health, education, dates, conversions, BD-specific units, and a complete Islamic suite."
      introBn="বাংলা ক্যালকুলেটর হলো বাংলাদেশের জন্য বিশেষভাবে তৈরি ৫১+ ক্যালকুলেটরের একটি বিনামূল্যের, দ্বিভাষিক সংগ্রহ — অর্থ, স্বাস্থ্য, শিক্ষা, তারিখ, রূপান্তর, বাংলাদেশ-নির্দিষ্ট একক এবং একটি সম্পূর্ণ ইসলামিক স্যুট।"
    >
      <ContentSection headingEn="Our Story" headingBn="আমাদের গল্প">
        <BiText
          en={
            <p>
              We kept reaching for foreign calculators that didn't speak our language — literally and figuratively.
              No katha or bigha. No NBR tax slabs. No Bengali calendar. No Hanafi-default Zakat. So we built the tool
              we wished existed: every calculator a Bangladeshi might actually need, in one place, free, bilingual.
            </p>
          }
          bn={
            <p>
              আমরা বারবার এমন বিদেশি ক্যালকুলেটর ব্যবহার করেছি যেগুলো আমাদের ভাষা — আক্ষরিক ও প্রাসঙ্গিক উভয় অর্থে — বোঝে না।
              কাঠা–বিঘা নেই, NBR কর স্ল্যাব নেই, বাংলা ক্যালেন্ডার নেই, হানাফি–ডিফল্ট যাকাত নেই। তাই আমরা সেই টুলটিই তৈরি করেছি
              যেটি আমরা চেয়েছিলাম: একজন বাংলাদেশির প্রয়োজনীয় প্রতিটি ক্যালকুলেটর, এক জায়গায়, বিনামূল্যে, দ্বিভাষিক।
            </p>
          }
        />
      </ContentSection>

      <ContentSection headingEn="Our Mission" headingBn="আমাদের লক্ষ্য">
        <BiText
          en={
            <p>
              To provide every calculator a Bangladeshi might need — secular, financial, educational, and Islamic —
              completely free and bilingual, without ads in the way of religious tools and without selling anyone's data.
            </p>
          }
          bn={
            <p>
              একজন বাংলাদেশির প্রয়োজনীয় প্রতিটি ক্যালকুলেটর — ধর্মনিরপেক্ষ, আর্থিক, শিক্ষাগত ও ইসলামিক — সম্পূর্ণ
              বিনামূল্যে ও দ্বিভাষিকভাবে দেওয়া। ধর্মীয় টুলে বিজ্ঞাপন ছাড়া, কারো তথ্য বিক্রি ছাড়া।
            </p>
          }
        />
      </ContentSection>

      <ContentSection headingEn="What we offer" headingBn="আমরা যা দিই">
        <BiText
          en={
            <p>51+ calculators across 7 categories: Finance, Islamic, Health, Date &amp; Time, Math, Converters, and Bangladesh-Specific.</p>
          }
          bn={
            <p>৭টি ক্যাটাগরিতে ৫১+ ক্যালকুলেটর: অর্থ, ইসলামিক, স্বাস্থ্য, তারিখ ও সময়, গণিত, রূপান্তরকারী, ও বাংলাদেশ-নির্দিষ্ট।</p>
          }
        />
      </ContentSection>

      <ContentSection headingEn="What makes us different" headingBn="আমরা কেন আলাদা">
        <ul className="ml-5 list-disc space-y-1.5">
          <BiText
            en={
              <>
                <li>BD-first context in every calculator</li>
                <li>Fully bilingual: English ↔ বাংলা, including Bengali numerals</li>
                <li>Free, no signup, no paywall — ever</li>
                <li>Bengali calendar + Hijri calendar + Gregorian three-way conversion</li>
                <li>BD units: katha, bigha, shotok, decimal, vori, mon, sher, tola</li>
                <li>NBR tax slabs (FY 2025-26), BD bank rates (DBBL, BRAC, EBL, City Bank)</li>
                <li>Hanafi madhhab default for Islamic calculators</li>
                <li>Karachi method for prayer times (BD standard)</li>
                <li>All 64 districts for Prayer Times, Sehri/Iftar, and Qibla</li>
              </>
            }
            bn={
              <>
                <li>প্রতিটি ক্যালকুলেটরে বাংলাদেশ-প্রথম প্রেক্ষাপট</li>
                <li>সম্পূর্ণ দ্বিভাষিক: ইংরেজি ↔ বাংলা, বাংলা সংখ্যা সহ</li>
                <li>বিনামূল্যে, সাইন-আপ নেই, কখনো পেওয়াল নেই</li>
                <li>বাংলা সন + হিজরি + গ্রেগরিয়ান তিন-পদ্ধতির রূপান্তর</li>
                <li>বাংলাদেশি একক: কাঠা, বিঘা, শতক, ডেসিমাল, ভরি, মণ, সের, তোলা</li>
                <li>NBR কর স্ল্যাব (অর্থবছর ২০২৫-২৬), বাংলাদেশি ব্যাংক হার (DBBL, BRAC, EBL, City)</li>
                <li>ইসলামিক ক্যালকুলেটরে হানাফি মাযহাব ডিফল্ট</li>
                <li>নামাজের সময়ে কারাচি পদ্ধতি (বাংলাদেশের মান)</li>
                <li>নামাজ, সেহরি/ইফতার, কিবলা — ৬৪টি জেলার জন্য</li>
              </>
            }
          />
        </ul>
      </ContentSection>

      <ContentSection headingEn="The team" headingBn="টিম">
        <BiText
          en={<p>Built by a small team of Bangladeshi developers and educators. Our Islamic suite is reviewed by qualified Mufti/scholars before launch and on every material change.</p>}
          bn={<p>একটি ছোট দল — বাংলাদেশি ডেভেলপার ও শিক্ষাবিদ — দ্বারা তৈরি। আমাদের ইসলামিক স্যুটটি লঞ্চের আগে ও প্রতিটি উল্লেখযোগ্য পরিবর্তনে যোগ্য মুফতি/আলেমদের দ্বারা পর্যালোচিত।</p>}
        />
      </ContentSection>

      <ContentSection headingEn="Religious accuracy commitment" headingBn="ধর্মীয় নির্ভুলতার অঙ্গীকার">
        <BiText
          en={
            <p>
              Our Islamic calculators are reviewed by qualified Mufti/scholars. We follow mainstream Hanafi consensus
              and cite Qur'anic and Hadith references where applicable. We treat Islamic content with the seriousness
              and accuracy it deserves.
            </p>
          }
          bn={
            <p>
              আমাদের ইসলামিক ক্যালকুলেটরগুলো যোগ্য মুফতি/আলেম দ্বারা পর্যালোচিত। আমরা মূলধারার হানাফি ঐকমত্য অনুসরণ করি
              এবং প্রযোজ্য ক্ষেত্রে কুরআন ও হাদিসের রেফারেন্স উল্লেখ করি। ইসলামিক কনটেন্টকে আমরা যথাযথ গুরুত্ব ও নির্ভুলতার সাথে বিবেচনা করি।
            </p>
          }
        />
      </ContentSection>

      <ContentSection headingEn="Contact" headingBn="যোগাযোগ">
        <BiText
          en={
            <p>
              Suggestions, bug reports, calculator requests, or scholarly feedback — visit{" "}
              <Link to="/contact" className="text-accent underline underline-offset-2">our contact page</Link>.
            </p>
          }
          bn={
            <p>
              পরামর্শ, বাগ রিপোর্ট, নতুন ক্যালকুলেটরের অনুরোধ, বা আলেমদের ফিডব্যাক — দেখুন{" "}
              <Link to="/contact" className="text-accent underline underline-offset-2">আমাদের যোগাযোগ পেজ</Link>।
            </p>
          }
        />
      </ContentSection>
    </ContentPage>
  );
}
